import React, { useCallback, useEffect } from 'react';
import Checkbox from '@atlaskit/checkbox';
import { colors, gridSize } from '@atlaskit/theme';
import styled from '@emotion/styled';
import { ConsentCheckboxState } from '../utilities/marketing-consent';
import { useIntl } from 'react-intl';
import messages from './MarketingConsentCheckbox.messages';
import { MarketingConsentConfig } from '../reducers/marketing-consent-reducer';
import { usePrevious } from '../utilities/hooks';

const MarketingConsentWrapper = styled.div`
  margin-top: ${gridSize()}px;
  div {
    align-items: flex-start;
  }
  [aria-label='checkboxIcon'] {
    margin-left: -8px;
    margin-top: -4px;
  }
  label {
    font-size: 12px;
    line-height: 16px;
    color: ${colors.N300};
  }
`;

interface Props {
  config: MarketingConsentConfig | null | undefined;
  forceShow?: boolean;
  className?: string;
  onConsentChange: (consent: ConsentCheckboxState) => void;
}

const MarketingConsentCheckbox = ({ className, config, forceShow, onConsentChange }: Props) => {
  const { formatMessage } = useIntl();

  const previousConfig = usePrevious(config);

  const consentText = formatMessage(messages.marketingConsentPromptText);

  const notifyConsentChange = useCallback(() => {
    let consent: ConsentCheckboxState = { state: 'not_shown' };
    if (config && config.showCheckbox) {
      consent = { state: 'unchecked', consentText: consentText };
    }
    onConsentChange(consent);
  }, [config, consentText, onConsentChange]);

  useEffect(() => {
    notifyConsentChange();
  }, [notifyConsentChange]);

  useEffect(() => {
    if (previousConfig !== config) {
      notifyConsentChange();
    }
  }, [previousConfig, config, notifyConsentChange]);

  const consentCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const state: 'checked' | 'unchecked' = e.target.checked ? 'checked' : 'unchecked';
    onConsentChange({ state, consentText: consentText } as ConsentCheckboxState);
  };

  if (!config || (!config.showCheckbox && !forceShow)) {
    return null;
  }

  return (
    <MarketingConsentWrapper className={className}>
      <Checkbox
        testId="marketing-consent-checkbox"
        id="marketing-consent-checkbox"
        label={consentText}
        onChange={consentCheckboxChanged}
      />
    </MarketingConsentWrapper>
  );
};

export default MarketingConsentCheckbox;
